import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Spinner,
    Alert,
    Badge
} from 'react-bootstrap';
import { FaServer, FaGlobe, FaCheck, FaArrowRight } from 'react-icons/fa';
import { BiCheckCircle } from 'react-icons/bi';
import { useTheme } from '../ThemeContext';
import './DataSourceSelector.css';
import { apifetch } from '../services/interceptors';

/**
 * DataSourceSelector Component
 * 
 * Allows users to select between internal scan results, external (domain) scan results,
 * or both when creating Custom Reports.
 * 
 * Props:
 * - onContinue: (selections: { internalScan: object | null, externalScan: object | null }) => void
 * - onBack: () => void (optional)
 * - organizationId: string (optional, for filtering internal scans)
 * - preSelectedScanPath: string (optional, file path of pre-selected internal scan from PastScans)
 * - preSelectedScanData: object (optional, scan data object from PastScans)
 */
function DataSourceSelector({
    onContinue,
    onBack,
    organizationId = null,
    preSelectedScanPath = null,
    preSelectedScanData = null
}) {
    const { isDarkMode } = useTheme();

    // Data source toggle states
    const [includeInternal, setIncludeInternal] = useState(false);
    const [includeExternal, setIncludeExternal] = useState(false);

    // Selected scan states
    const [selectedInternalScan, setSelectedInternalScan] = useState(null);
    const [selectedExternalScan, setSelectedExternalScan] = useState(null);

    // Loading and error states
    const [internalScans, setInternalScans] = useState([]);
    const [externalScans, setExternalScans] = useState([]);
    const [loadingInternal, setLoadingInternal] = useState(false);
    const [loadingExternal, setLoadingExternal] = useState(false);
    const [errorInternal, setErrorInternal] = useState(null);
    const [errorExternal, setErrorExternal] = useState(null);

    // Fetch internal scans - filters by organization if organizationId is provided
    const fetchInternalScans = useCallback(async () => {
        setLoadingInternal(true);
        setErrorInternal(null);

        try {
            const token = localStorage.getItem('token');

            // Build request body - include organizationId if provided
            const requestBody = organizationId ? { organization_id: organizationId } : {};

            console.log('[DataSourceSelector] Fetching internal scans', organizationId ? `for organization ${organizationId}` : 'for all organizations');

            const response = await apifetch('/s2/api/all_scan_results', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error('Failed to fetch internal scans');
            }

            const data = await response.json();

            // Backend now filters by organization_id, so we can use the results directly
            const scans = data.result || [];
            console.log('[DataSourceSelector] Received', scans.length, 'scan(s) from backend');

            setInternalScans(scans);
        } catch (err) {
            console.error('[DataSourceSelector] Error fetching internal scans:', err);
            setErrorInternal(err.message);
        } finally {
            setLoadingInternal(false);
        }
    }, [organizationId]);

    // Fetch external scans
    const fetchExternalScans = useCallback(async () => {
        setLoadingExternal(true);
        setErrorExternal(null);

        try {
            const token = localStorage.getItem('token');
            const requestBody = organizationId ? { organizationId: parseInt(organizationId, 10) } : {};

            const response = await apifetch('/s2/api/external_risk', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error('Failed to fetch external scans');
            }

            const data = await response.json();
            setExternalScans(data.result || []);
        } catch (err) {
            console.error('[DataSourceSelector] Error fetching external scans:', err);
            setErrorExternal(err.message);
        } finally {
            setLoadingExternal(false);
        }
    }, [organizationId]);

    // Fetch scans on mount
    useEffect(() => {
        fetchInternalScans();
        fetchExternalScans();
    }, [fetchInternalScans, fetchExternalScans]);

    // Handle pre-selected scan from PastScans
    useEffect(() => {
        if (preSelectedScanPath && internalScans.length > 0) {
            console.log('[DataSourceSelector] Pre-selecting internal scan:', preSelectedScanPath);

            // Try to find the scan in the fetched scans list
            const matchingScan = internalScans.find(scan => scan.file_path === preSelectedScanPath);

            if (matchingScan) {
                // Found exact match in the list
                console.log('[DataSourceSelector] Found matching scan in list');
                setSelectedInternalScan(matchingScan);
                setIncludeInternal(true);
            } else {
                // Create a scan object from the pre-selected data
                console.log('[DataSourceSelector] Creating scan object from pre-selected data');
                const preSelectedScan = {
                    file_path: preSelectedScanPath,
                    scan_id: 'from-past-scans',
                    created_at: new Date().toISOString(),
                    ...(preSelectedScanData && typeof preSelectedScanData === 'object' ? preSelectedScanData : {})
                };
                setSelectedInternalScan(preSelectedScan);
                setIncludeInternal(true);
            }
        }
    }, [preSelectedScanPath, preSelectedScanData, internalScans]);

    // Handle internal toggle
    const handleInternalToggle = () => {
        const newValue = !includeInternal;
        setIncludeInternal(newValue);
        if (!newValue) {
            setSelectedInternalScan(null);
        } else if (internalScans.length > 0 && !selectedInternalScan) {
            // Auto-select first scan when enabling
            setSelectedInternalScan(internalScans[0]);
        }
    };

    // Handle external toggle
    const handleExternalToggle = () => {
        const newValue = !includeExternal;
        setIncludeExternal(newValue);
        if (!newValue) {
            setSelectedExternalScan(null);
        } else if (externalScans.length > 0 && !selectedExternalScan) {
            // Auto-select first scan when enabling
            setSelectedExternalScan(externalScans[0]);
        }
    };

    // Handle internal scan selection
    const handleInternalScanChange = (e) => {
        const scanId = e.target.value;
        const scan = internalScans.find(s => String(s.scan_id) === scanId);
        setSelectedInternalScan(scan || null);
    };

    // Handle external scan selection
    const handleExternalScanChange = (e) => {
        const scanId = e.target.value;
        const scan = externalScans.find(s => String(s.scan_id) === scanId);
        setSelectedExternalScan(scan || null);
    };

    // Check if continue button should be enabled
    const canContinue = (includeInternal && selectedInternalScan) ||
        (includeExternal && selectedExternalScan);

    // Handle continue
    const handleContinue = () => {
        if (!canContinue) return;

        onContinue({
            internalScan: includeInternal ? selectedInternalScan : null,
            externalScan: includeExternal ? selectedExternalScan : null
        });
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown Date';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Unknown Date';
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Get score variant for badge
    const getScoreVariant = (score) => {
        const numericScore = parseInt(score, 10);
        if (numericScore >= 70) return 'danger';
        if (numericScore >= 40) return 'warning';
        return 'success';
    };

    // Extract filename from path
    const getFileName = (filePath) => {
        if (!filePath) return 'Unknown';
        const parts = filePath.split('/');
        return parts[parts.length - 1] || 'Unknown';
    };

    return (
        <div className={`data-source-selector ${isDarkMode ? 'dark-mode' : ''}`}>
            <Container className="py-4">
                {/* Header */}
                <div className="text-center mb-4">
                    <h4 className={isDarkMode ? 'text-light' : ''}>
                        Select Data Sources
                    </h4>
                    <p className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                        Choose which scan results to include in your report
                    </p>
                </div>

                {/* Data Source Cards */}
                <Row className="g-4 justify-content-center mb-4">
                    {/* Internal Scan Card */}
                    <Col xs={12} md={6} lg={5}>
                        <Card
                            className={`data-source-card h-100 ${includeInternal ? 'selected' : ''} ${isDarkMode ? 'dark-card' : ''}`}
                            onClick={handleInternalToggle}
                        >
                            <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className={`data-source-icon ${includeInternal ? 'active' : ''}`}>
                                        <FaServer size={32} />
                                    </div>
                                    {includeInternal && (
                                        <div className="selected-check">
                                            <BiCheckCircle size={24} className="text-success" />
                                        </div>
                                    )}
                                </div>

                                <Card.Title className={isDarkMode ? 'text-light' : ''}>
                                    Internal Risk Scan
                                </Card.Title>
                                <Card.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                    Network, Active Directory, MS365, and localhost scan results
                                </Card.Text>

                                {/* Internal Scan Selector */}
                                {includeInternal && (
                                    <div className="scan-selector mt-3" onClick={(e) => e.stopPropagation()}>
                                        {loadingInternal ? (
                                            <div className="text-center py-2">
                                                <Spinner animation="border" size="sm" />
                                                <span className="ms-2">Loading scans...</span>
                                            </div>
                                        ) : errorInternal ? (
                                            <Alert variant="danger" className="py-2 mb-0">
                                                {errorInternal}
                                            </Alert>
                                        ) : internalScans.length === 0 ? (
                                            <Alert variant="info" className="py-2 mb-0">
                                                No internal scans available
                                            </Alert>
                                        ) : (
                                            <>
                                                <Form.Select
                                                    value={selectedInternalScan?.scan_id || ''}
                                                    onChange={handleInternalScanChange}
                                                    className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                                                >
                                                    <option value="">Select a scan...</option>
                                                    {internalScans.map((scan) => (
                                                        <option key={scan.scan_id} value={scan.scan_id}>
                                                            {getFileName(scan.file_path)} - {formatDate(scan.created_at)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {selectedInternalScan && (
                                                    <div className="selected-scan-info mt-2">
                                                        <Badge bg={getScoreVariant(selectedInternalScan.score)}>
                                                            Risk: {selectedInternalScan.score}%
                                                        </Badge>
                                                        <span className={`ms-2 small ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
                                                            by {selectedInternalScan.username || 'Unknown'}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="scan-count mt-3">
                                    <Badge bg="secondary">
                                        {internalScans.length} scan{internalScans.length !== 1 ? 's' : ''} available
                                    </Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* External Scan Card */}
                    <Col xs={12} md={6} lg={5}>
                        <Card
                            className={`data-source-card h-100 ${includeExternal ? 'selected' : ''} ${isDarkMode ? 'dark-card' : ''}`}
                            onClick={handleExternalToggle}
                        >
                            <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className={`data-source-icon external ${includeExternal ? 'active' : ''}`}>
                                        <FaGlobe size={32} />
                                    </div>
                                    {includeExternal && (
                                        <div className="selected-check">
                                            <BiCheckCircle size={24} className="text-success" />
                                        </div>
                                    )}
                                </div>

                                <Card.Title className={isDarkMode ? 'text-light' : ''}>
                                    External Risk Scan
                                </Card.Title>
                                <Card.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                    Domain security, DNS, SSL/TLS, and security headers scan
                                </Card.Text>

                                {/* External Scan Selector */}
                                {includeExternal && (
                                    <div className="scan-selector mt-3" onClick={(e) => e.stopPropagation()}>
                                        {loadingExternal ? (
                                            <div className="text-center py-2">
                                                <Spinner animation="border" size="sm" />
                                                <span className="ms-2">Loading scans...</span>
                                            </div>
                                        ) : errorExternal ? (
                                            <Alert variant="danger" className="py-2 mb-0">
                                                {errorExternal}
                                            </Alert>
                                        ) : externalScans.length === 0 ? (
                                            <Alert variant="info" className="py-2 mb-0">
                                                No external scans available
                                            </Alert>
                                        ) : (
                                            <>
                                                <Form.Select
                                                    value={selectedExternalScan?.scan_id || ''}
                                                    onChange={handleExternalScanChange}
                                                    className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                                                >
                                                    <option value="">Select a scan...</option>
                                                    {externalScans.map((scan) => (
                                                        <option key={scan.scan_id} value={scan.scan_id}>
                                                            {scan.domain || getFileName(scan.file_path)} - {formatDate(scan.created_at)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {selectedExternalScan && (
                                                    <div className="selected-scan-info mt-2">
                                                        <Badge bg={getScoreVariant(selectedExternalScan.score)}>
                                                            Risk: {selectedExternalScan.score}%
                                                        </Badge>
                                                        <span className={`ms-2 small ${isDarkMode ? 'text-light opacity-75' : 'text-muted'}`}>
                                                            Domain: {selectedExternalScan.domain || 'N/A'}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="scan-count mt-3">
                                    <Badge bg="secondary">
                                        {externalScans.length} scan{externalScans.length !== 1 ? 's' : ''} available
                                    </Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Selection Summary */}
                {(includeInternal || includeExternal) && (
                    <div className={`selection-summary text-center mb-4 ${isDarkMode ? 'dark-summary' : ''}`}>
                        <p className={isDarkMode ? 'text-light mb-0' : 'mb-0'}>
                            <strong>Selected:</strong>{' '}
                            {includeInternal && selectedInternalScan && (
                                <Badge bg="primary" className="me-2">
                                    <FaServer className="me-1" />
                                    Internal: {getFileName(selectedInternalScan.file_path).substring(0, 20)}...
                                </Badge>
                            )}
                            {includeExternal && selectedExternalScan && (
                                <Badge bg="info">
                                    <FaGlobe className="me-1" />
                                    External: {selectedExternalScan.domain || 'Domain Scan'}
                                </Badge>
                            )}
                        </p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-between align-items-center">
                    {onBack && (
                        <Button
                            variant={isDarkMode ? "outline-light" : "outline-secondary"}
                            onClick={onBack}
                        >
                            ← Back
                        </Button>
                    )}
                    <div className={!onBack ? 'ms-auto' : ''}>
                        <Button
                            variant="primary"
                            size="lg"
                            disabled={!canContinue}
                            onClick={handleContinue}
                            className="d-flex align-items-center"
                        >
                            Continue to Template Selection
                            <FaArrowRight className="ms-2" />
                        </Button>
                    </div>
                </div>

                {/* Help Text */}
                <div className="text-center mt-4">
                    <small className={isDarkMode ? 'text-light opacity-50' : 'text-muted'}>
                        💡 <strong>Tip:</strong> Select both data sources to create a comprehensive report
                        combining internal infrastructure and external domain security findings.
                    </small>
                </div>
            </Container>
        </div>
    );
}

export default DataSourceSelector;
